import { ParsingUnsuccessful, UnknownRecipeIdentifier } from './errors'
import FormulaParser from './formula-parser'
import Resolve from './resolve'

const cytoscape = require('cytoscape')

export default class FieldCalculator {
  constructor (symbols = {}, functions = {}) {
    this.graph = cytoscape({ headless: false })
    this.sorted = []
    // Init symbol and result tables
    this.symbols = symbols
    this.functions = functions
    this.resolve = new Resolve(symbols, functions)
  }

  /**
   * Add a dependency from source to target.
   * @param source id of the source node
   * @param target id of the target node
   */
  addDependency (source, target) {
    this.graph.add({
      group: 'edges',
      data: {
        source: source,
        target: target,
      },
    })
  }

  /**
   * Add a recipe to the execution graph.
   *
   * @param identifier The identifier of the recipe, to be accessed by other recipes.
   * @param recipe The recipe to add to the execution graph.
   * @param handler The recipe handler which provides methods that control the behavior of this recipe.
   * @param nodeId Id of the node. Defaults to identifier.
   * @param parentNode the parent node in the graph (for list fields).
   * @param onRegister
   */
  addRecipeNode ({ identifier, recipe, handler }, nodeId = null, parentNode = null, onRegister = null) {
    // Node ID: identifier for top-level fields, identifier[i].childIdentifier for list fields
    // a list field node corresponds to "all values of the list", e.g. a list dependency requires all of its children
    // to be dependencies also.
    nodeId = nodeId || identifier
    // Add node for field
    const node = this.graph.add({
      group: 'nodes',
      data: {
        id: nodeId,
        recipe,
        handler,
        getValue: () => handler.getValue(),
        putValue: value => handler.putValue(value),
        scope: parentNode ? [...parentNode.data().scope, parentNode.data().id] : [], // The ids of the parent nodes (the scope)
        scopedAliases: [identifier], // Names of the scoped aliases this node provides
        scopedSymbols: parentNode ? parentNode.data().scopedSymbols : {}, // Scoped symbols this node provides
      },
    })

    if (typeof handler === 'object') {
      const childHandler = handler.getChildHandler()
      // Check if the recipe node provides child nodes via the handler object.
      if (childHandler !== null && typeof childHandler === 'object' && childHandler.hasItems()) {
        const items = childHandler.getItems()
        // For each entry in the list field....
        for (const index in items) {
          const item = items[index]
          this.addChildNode(item, index, handler, childHandler, node, nodeId, onRegister)
        }
      }
    }

    // Call field register callback
    if (typeof onRegister === 'function') {
      onRegister(arguments[0], node)
    }
    if (typeof handler.nodeRegistered === 'function') {
      handler.nodeRegistered(node)
    }
  }

  /**
   * @param {*} item
   * @param {*} index
   * @param {null, {getScopedSymbols, getItems, getValue, putValue, getFields, getFieldValue, putFieldValue}} handler
   * @param {null, {getScopedSymbols, getItems, getValue, putValue, getFields, getFieldValue, putFieldValue}} childHandler
   * @param node
   * @param {*} nodeId
   * @param onRegister
   */
  addChildNode (item, index, handler, childHandler, node, nodeId, onRegister = null) {
    // Declare get, put and child specs functions
    const getValue = () => childHandler.getValue(item)
    const putValue = value => childHandler.putValue(item, index, value)
    const getChildren = () => childHandler.getChildren(item)
    const childScopedSymbols = childHandler.getScopedSymbols(item, index)

    // Add node for entry: listField[index]
    const entryNodeId = `${nodeId}[${index}]`

    // Add new scoped symbols to recently scoped symbols by node
    let scopedSymbols = Object.assign({}, node.data().scopedSymbols)
    scopedSymbols = Object.assign(scopedSymbols, childScopedSymbols)

    // Add entry node for child item
    const entryNode = this.graph.add({
      group: 'nodes',
      data: {
        id: entryNodeId,
        handler,
        getValue,
        putValue,
        scope: [...node.data().scope, nodeId],
        scopedAliases: [],
        scopedSymbols: scopedSymbols,
      },
    })

    if (typeof childHandler.nodeRegistered === 'function') {
      childHandler.nodeRegistered(entryNode, item)
    }

    // Add a dependency listField[index] -> listField
    this.addDependency(entryNodeId, nodeId)

    // And for each child field...
    for (const child of getChildren()) {
      // Add a field node: listField[index].childField
      const childNodeId = `${nodeId}[${index}].${child.identifier}`
      this.addRecipeNode(child, childNodeId, entryNode, onRegister)
      // Add a dependency listField[index].childField -> listField[index]
      this.addDependency(childNodeId, entryNodeId)
    }
  }

  /**
   * Get all the identifiers and their nodes that are available for the specified node (e.g. in the same scope)
   * @param currentNode current node in which scope to look for
   */
  getIdentifiersForNode (currentNode) {
    let identifiers = {}
    if (currentNode.data().scope.length === 0) {
      // currentNode is in global scope
      this.graph.nodes().forEach(node => {
        if (node.data().scope.length === 0) {
          identifiers[node.data().id] = node
          node.data().scopedAliases.forEach(scopedVar => {
            identifiers[scopedVar] = node
          })
        }
      })
    }
    this.graph.nodes().forEach(node => {
      const id = node.data().id
      const currentScope = currentNode.data().scope
      const nodeScope = node.data().scope
      if (currentScope.length > 0 && nodeScope.length > 0 && currentScope[currentScope.length - 1] === nodeScope[nodeScope.length - 1] &&
        currentNode.data().id !== node.data().id) {
        // currentNode should know all aliases of its siblings
        node.data().scopedAliases.forEach(scopedVar => {
          identifiers[scopedVar] = node
        })
      }
      if (currentNode.data().scope.includes(id)) {
        // currentNode should know all aliases that its parent node ("scope") knows
        identifiers = Object.assign(identifiers,
          this.getIdentifiersForNode(node))
      }
    })
    return identifiers
  }

  /**
   * Build the execution graph by connecting nodes with their dependencies.
   */
  build () {
    const formulaParser = new FormulaParser()
    this.graph.nodes().forEach(node => {
      const nodeId = node.data().id
      const recipe = node.data().recipe
      if (recipe) {
        const handler = node.data().handler
        const scope = typeof handler.getScope === 'function' ? handler.getScope() : null
        // Parse formula
        const results = formulaParser.parse(recipe)[0]
        if (typeof results === 'undefined') {
          console.error(new ParsingUnsuccessful(recipe).message)
        }
        const vars = Array.from(
          results.map(result => formulaParser.getVars(result))
            .reduce((acc, current) => {
              current.forEach(item => acc.add(item))
              return acc
            }, new Set()))
          .filter(variable => !(variable in this.resolve.functionTable))
          .filter(variable => !(variable in this.resolve.constants))
        node.data().evalTree = results
        const identifierNodes = this.getIdentifiersForNode(node) // TODO: make this faster
        vars.forEach(identifier => {
          // Get corresponding node for identifier
          const sourceNode = identifierNodes[identifier]
          // Add edge variable -> field
          if (sourceNode) {
            // The variable is indeed pointing to another field node and is not a pre-defined symbol.
            this.addDependency(sourceNode.data().id, nodeId)
          }
          if (!sourceNode && !this.symbols.hasOwnProperty(identifier) && !scope) {
            console.warn(new UnknownRecipeIdentifier(identifier, recipe).message)
          }
        })
        if (scope) {
          this.addDependency(scope, nodeId)
        }
      }
    })
    // Sort the execution graph topologically
    this.sorted = this.topologicalSort(this.graph)
  }

  /**
   * Kahn's algorithm for topological sorting.
   * https://en.wikipedia.org/wiki/Topological_sorting
   *
   * @param graph
   */
  topologicalSort (graph) {
    const g = cytoscape({ headless: true })
    g.add(graph.nodes().clone())
    g.add(graph.edges().clone())
    const list = []
    const startNodes = g.nodes().roots().toArray()
    while (startNodes.length > 0) {
      const n = startNodes.pop()
      list.push(n)
      n.outgoers().edges().forEach(e => {
        const m = e.target()
        e.remove()
        if (m.incomers().length === 0) {
          startNodes.push(m)
        }
      })
    }
    if (g.edges().length > 0) {
      throw new Error(
        'The fields graph is not acyclic. Circular references exist between fields.')
    }
    return list
  }

  calculate (recipe) {
    const symbolTable = Object.assign({}, this.symbols)
    const resolve = new Resolve(symbolTable)
    const formulaParser = new FormulaParser()
    const evalTree = formulaParser.parse(recipe)[0]
    return evalTree[1].v(resolve)
  }

  /**
   * Calculate all fields by
   * (1) iterating through the sorted list
   * (2) calculating each value by the field's formula
   * (3) storing all values in a symbol table.
   */
  run () {
    // Init symbol and result tables
    const symbolTable = Object.assign({}, this.symbols)
    const functionTable = Object.assign({}, this.functions)
    // Define resolve functions for the evaluation tree
    const resolve = new Resolve(symbolTable, functionTable)
    // Define scopes
    const rootScope = Object.assign({}, resolve.symbolTable)
    const scopes = {}
    this.sorted.forEach(node => {
      const nodeId = node.data().id
      const scopeIds = node.data().scope
      let value = node.data().getValue()

      let writeScopeId = null
      if (scopeIds.length > 0) {
        writeScopeId = scopeIds[scopeIds.length - 1]
        if (!scopes[writeScopeId]) {
          scopes[writeScopeId] = {}
        }
      }

      const writeScope = writeScopeId ? scopes[writeScopeId] : rootScope
      // Use current scope
      resolve.symbolTable = scopeIds.reduce(
        (previousValue, scopeId) => Object.assign(previousValue, scopes[scopeId] || {}),
        Object.assign({}, rootScope, node.data().scopedSymbols)
      )

      if (node.data().evalTree) {
        // Calculate node value
        resolve.context = node.data()
        value = node.data().evalTree[1].v(resolve)
        if (Number.isNaN(value)) {
          value = 0
        }
      }

      node.data().putValue(value)
      // Store value in scope
      writeScope[nodeId] = value

      // Store scoped vars in symbol table
      node.data().scopedAliases.forEach(identifier => {
        writeScope[identifier] = value
      })
    })
  }
}
